"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.captureUserFeedback = captureUserFeedback;
exports.forceLoad = forceLoad;
exports.getDefaultIntegrations = getDefaultIntegrations;
exports.init = init;
exports.onLoad = onLoad;
exports.showReportDialog = showReportDialog;

var _core = require("@sentry/core");

var _client = require("./client.js");

var _debugBuild = require("./debug-build.js");

var _helpers = require("./helpers.js");

var _breadcrumbs = require("./integrations/breadcrumbs.js");

var _browserapierrors = require("./integrations/browserapierrors.js");

var _browsersession = require("./integrations/browsersession.js");

var _globalhandlers = require("./integrations/globalhandlers.js");

var _httpcontext = require("./integrations/httpcontext.js");

var _linkederrors = require("./integrations/linkederrors.js");

var _stackParsers = require("./stack-parsers.js");

var _fetch = require("./transports/fetch.js");

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/** Get the default integrations for the browser SDK. */
function getDefaultIntegrations(options) {
  /**
   * Note: Please make sure this stays in sync with Angular SDK, which re-exports
   * `getDefaultIntegrations` but with an adjusted set of integrations.
   */
  const integrations = [(0, _core.inboundFiltersIntegration)(), (0, _core.functionToStringIntegration)(), (0, _browserapierrors.browserApiErrorsIntegration)(), (0, _breadcrumbs.breadcrumbsIntegration)(), (0, _globalhandlers.globalHandlersIntegration)(), (0, _linkederrors.linkedErrorsIntegration)(), (0, _core.dedupeIntegration)(), (0, _httpcontext.httpContextIntegration)()]; // eslint-disable-next-line deprecation/deprecation

  if (options.autoSessionTracking !== false) {
    integrations.push((0, _browsersession.browserSessionIntegration)());
  }

  return integrations;
}

function applyDefaultOptions(optionsArg = {}) {
  const defaultOptions = {
    defaultIntegrations: getDefaultIntegrations(optionsArg),
    release: typeof __SENTRY_RELEASE__ === 'string' // This allows build tooling to find-and-replace __SENTRY_RELEASE__ to inject a release value
    ? __SENTRY_RELEASE__ : _helpers.WINDOW.SENTRY_RELEASE && _helpers.WINDOW.SENTRY_RELEASE.id // This supports the variable that sentry-webpack-plugin injects
    ? _helpers.WINDOW.SENTRY_RELEASE.id : undefined,
    autoSessionTracking: true,
    sendClientReports: true
  }; // TODO: Instead of dropping just `defaultIntegrations`, we should simply
  // call `dropUndefinedKeys` on the entire `optionsArg`.
  // However, for this to work we need to adjust the `hasTracingEnabled()` logic
  // first as it differentiates between `undefined` and the key not being in the object.

  if (optionsArg.defaultIntegrations == null) {
    delete optionsArg.defaultIntegrations;
  }

  return _objectSpread(_objectSpread({}, defaultOptions), optionsArg);
}

function shouldShowBrowserExtensionError() {
  const windowWithMaybeExtension = typeof _helpers.WINDOW.window !== 'undefined' && _helpers.WINDOW;

  if (!windowWithMaybeExtension) {
    // No need to show the error if we're not in a browser window environment (e.g. service workers)
    return false;
  }

  const extensionKey = windowWithMaybeExtension.chrome ? 'chrome' : 'browser';
  const extensionObject = windowWithMaybeExtension[extensionKey];
  const runtimeId = extensionObject && extensionObject.runtime && extensionObject.runtime.id;
  const href = _helpers.WINDOW.location && _helpers.WINDOW.location.href || '';
  const extensionProtocols = ['chrome-extension:', 'moz-extension:', 'ms-browser-extension:', 'safari-web-extension:']; // Running the SDK in a dedicated extension page and calling Sentry.init is fine; no risk of data leakage

  const isDedicatedExtensionPage = !!runtimeId && _helpers.WINDOW === _helpers.WINDOW.top && extensionProtocols.some(protocol => href.startsWith(`${protocol}//`)); // Running the SDK in NW.js, which appears like a browser extension but isn't, is also fine
  // see: https://github.com/getsentry/sentry-javascript/issues/12668

  const isNWjs = typeof windowWithMaybeExtension.nw !== 'undefined';
  return !!runtimeId && !isDedicatedExtensionPage && !isNWjs;
}
/**
 * A magic string that build tooling can leverage in order to inject a release value into the SDK.
 */

/**
 * The Sentry Browser SDK Client.
 *
 * To use this SDK, call the {@link init} function as early as possible when
 * loading the web page. To set context information or send manual events, use
 * the provided methods.
 *
 * @example
 *
 * ```
 *
 * import { init } from '@sentry/browser';
 *
 * init({
 *   dsn: '__DSN__',
 *   // ...
 * });
 * ```
 *
 * @example
 * ```
 *
 * import { addBreadcrumb } from '@sentry/browser';
 * addBreadcrumb({
 *   message: 'My Breadcrumb',
 *   // ...
 * });
 * ```
 *
 * @example
 *
 * ```
 *
 * import * as Sentry from '@sentry/browser';
 * Sentry.captureMessage('Hello, world!');
 * Sentry.captureException(new Error('Good bye'));
 * Sentry.captureEvent({
 *   message: 'Manual',
 *   stacktrace: [
 *     // ...
 *   ],
 * });
 * ```
 *
 * @see {@link BrowserOptions} for documentation on configuration options.
 */


function init(browserOptions = {}) {
  const options = applyDefaultOptions(browserOptions);

  if (!options.skipBrowserExtensionCheck && shouldShowBrowserExtensionError()) {
    (0, _core.consoleSandbox)(() => {
      // eslint-disable-next-line no-console
      console.error('[Sentry] You cannot run Sentry this way in a browser extension, check: https://docs.sentry.io/platforms/javascript/best-practices/browser-extensions/');
    });
    return;
  }

  if (_debugBuild.DEBUG_BUILD) {
    if (!(0, _core.supportsFetch)()) {
      _core.logger.warn('No Fetch API detected. The Sentry SDK requires a Fetch API compatible environment to send events. Please add a Fetch API polyfill.');
    }
  }

  const clientOptions = _objectSpread(_objectSpread({}, options), {}, {
    stackParser: (0, _core.stackParserFromStackParserOptions)(options.stackParser || _stackParsers.defaultStackParser),
    integrations: (0, _core.getIntegrationsToSetup)(options),
    transport: options.transport || _fetch.makeFetchTransport
  });

  return (0, _core.initAndBind)(_client.BrowserClient, clientOptions);
}
/**
 * All properties the report dialog supports
 */

/**
 * Present the user with a report dialog.
 *
 * @param options Everything is optional, we try to fetch all info need from the global scope.
 */


function showReportDialog(options = {}) {
  // doesn't work without a document (React Native)
  if (!_helpers.WINDOW.document) {
    _debugBuild.DEBUG_BUILD && _core.logger.error('Global document not defined in showReportDialog call');
    return;
  }

  const scope = (0, _core.getCurrentScope)();
  const client = scope.getClient();
  const dsn = client && client.getDsn();

  if (!dsn) {
    _debugBuild.DEBUG_BUILD && _core.logger.error('DSN not configured for showReportDialog call');
    return;
  }

  if (scope) {
    options.user = _objectSpread(_objectSpread({}, scope.getUser()), options.user);
  }

  if (!options.eventId) {
    const eventId = (0, _core.lastEventId)();

    if (eventId) {
      options.eventId = eventId;
    }
  }

  const script = _helpers.WINDOW.document.createElement('script');

  script.async = true;
  script.crossOrigin = 'anonymous';
  script.src = (0, _core.getReportDialogEndpoint)(dsn, options);

  if (options.onLoad) {
    script.onload = options.onLoad;
  }

  const onClose = options.onClose;

  if (onClose) {
    const reportDialogClosedMessageHandler = event => {
      if (event.data === '__sentry_reportdialog_closed__') {
        try {
          onClose();
        } finally {
          _helpers.WINDOW.removeEventListener('message', reportDialogClosedMessageHandler);
        }
      }
    };

    _helpers.WINDOW.addEventListener('message', reportDialogClosedMessageHandler);
  }

  const injectionPoint = _helpers.WINDOW.document.head || _helpers.WINDOW.document.body;

  if (injectionPoint) {
    injectionPoint.appendChild(script);
  } else {
    _debugBuild.DEBUG_BUILD && _core.logger.error('Not injecting report dialog. No injection point found in HTML');
  }
}
/**
 * This function is here to be API compatible with the loader.
 * @hidden
 */


function forceLoad() {// Noop
}
/**
 * This function is here to be API compatible with the loader.
 * @hidden
 */


function onLoad(callback) {
  callback();
}
/**
 * Captures user feedback and sends it to Sentry.
 *
 * @deprecated Use `captureFeedback` instead.
 */


function captureUserFeedback(feedback) {
  const client = (0, _core.getClient)();

  if (client) {
    // eslint-disable-next-line deprecation/deprecation
    client.captureUserFeedback(feedback);
  }
}